// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage from "@vanmeter/MobiusOverrides/LazyImage";
import { fontConfig, sharedDateTimeOptions } from "@vanmeter/XDL/constants";
import { CenteringWrapper, color, ellipsis } from "@vanmeter/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import CardListHeading, { CardListHeadingStyles } from "@vanmeter/Overrides/Components/CardListHeading";
import { css } from "styled-components";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import MyListsEditListForm from "@insite/content-library/Widgets/MyLists/MyListsEditListForm";
import Modal from "@insite/mobius/Modal";
import Button, { ButtonPresentationProps } from "@vanmeter/MobiusOverrides/Button/Button";

interface OwnProps {
    isLoading: boolean;
    wishLists?: WishListModel[];
    myListsPageUrl?: string;
}
const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state.context.session.language,
        myListsDetailsPage: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface RecentWishListsCondensedStyles {
    listWrapper?: InjectableCss;
    titleWrapper?: CardListHeadingStyles;
    lineItem?: InjectableCss;
    lineImage?: LazyImageProps;
    lineTitle?: TypographyPresentationProps;
    lineUpdatedInfo?: TypographyPresentationProps;
    lineInfoWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    buttonStyle?: ButtonPresentationProps;
}

export const recentWishListsCondensedStyles: RecentWishListsCondensedStyles = {
    listWrapper: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-radius: 4px;
            display: flex;
            flex-direction: column;
            gap: 12px;
        `,
    },
    titleWrapper: {
        wrapper: {
            css: css`
                display: flex;
                width: 100%;
                align-items: center;
                justify-content: space-between;
                padding: 12px;
                background-color: ${color("primary")};
            `,
        },
        title: {
            variant: "h5",
            forwardAs: "p",
            color: "white",
            css: css`
                margin: 0;
            `,
        },
        viewAllLink: {
            typographyProps: {
                ...fontConfig.xSmallSingleLine,
            },
            variant: "secondary",
        },
    },
    lineItem: {
        css: css`
            display: flex;
            gap: 12px;
            margin: 0 12px;
            padding-bottom: 12px;
            &:not(:last-child) {
                border-bottom: 1px solid ${color("common.border")};
            }
        `,
    },
    lineImage: {
        css: css`
            aspect-ratio: 1;
            width: 52px;
            min-width: 52px;

            img {
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    lineInfoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    lineTitle: {
        ...fontConfig.xSmallSingleLine,
        css: css`
            ${ellipsis(2)}
        `,
    },
    lineUpdatedInfo: {
        ...fontConfig.xSmallSingleLine,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
        `,
    },
    buttonStyle: {
        variant: "primary",
        buttonType: "outline",
        css: css`
            width: fit-content;
            margin: 0 auto 12px;
        `,
    },
};

const styles = recentWishListsCondensedStyles;

const RecentWishListsCondensedView: FC<Props> = ({
    isLoading,
    wishLists,
    language,
    myListsPageUrl,
    myListsDetailsPage,
}) => {
    const [createListModalIsOpen, setCreateListModalIsOpen] = React.useState(false);

    const modalCloseHandler = () => {
        setCreateListModalIsOpen(false);
    };

    return (
        <>
            <StyledWrapper {...styles.listWrapper}>
                <CardListHeading
                    heading={translate("My Recent Lists")}
                    viewAllUrl={myListsPageUrl}
                    viewAllLabel={translate("View All Lists")}
                    extendedStyles={styles.titleWrapper}
                />
                {!isLoading && wishLists?.length ? (
                    <>
                        {wishLists?.map(list => {
                            const date = getLocalizedDateTime({
                                dateTime: new Date(list.updatedOn),
                                language,
                                options: {
                                    ...sharedDateTimeOptions,
                                },
                            });
                            return (
                                <StyledWrapper {...styles.lineItem} key={list.id}>
                                    {list.wishListLineCollection?.[0]?.productUri ? (
                                        <Clickable
                                            href={list.wishListLineCollection?.[0]?.productUri}
                                            aria-label={translate("my list product image")}
                                        >
                                            <LazyImage
                                                src={list.wishListLineCollection?.[0]?.smallImagePath}
                                                altText={list.description}
                                                {...styles.lineImage}
                                            />
                                        </Clickable>
                                    ) : (
                                        <LazyImage
                                            src={list.wishListLineCollection?.[0]?.smallImagePath}
                                            altText={list.description}
                                            {...styles.lineImage}
                                        />
                                    )}
                                    <StyledWrapper {...styles.lineInfoWrapper}>
                                        <Link
                                            href={`${myListsDetailsPage}?id=${list.id}`}
                                            typographyProps={styles.lineTitle}
                                            data-test-selector="list-name-link"
                                            aria-label={list.name}
                                        >
                                            {list.name}
                                        </Link>
                                        <Typography
                                            {...styles.lineUpdatedInfo}
                                        >{`${translate("Updated")} ${date} ${translate(
                                            "by",
                                        )} ${list.updatedByDisplayName}`}</Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </>
                ) : (
                    <>
                        {!isLoading && wishLists?.length === 0 ? (
                            <Button
                                onClick={() => {
                                    setCreateListModalIsOpen(true);
                                }}
                                aria-label={translate("Create Your First List")}
                                {...styles.buttonStyle}
                            >
                                {translate("Create Your First List")}
                            </Button>
                        ) : (
                            <CenteringWrapper height={256} {...styles.centeringWrapper}>
                                <LoadingSpinner />
                            </CenteringWrapper>
                        )}
                    </>
                )}
            </StyledWrapper>
            <Modal headline={translate("Create List")} isOpen={createListModalIsOpen} handleClose={modalCloseHandler}>
                <MyListsEditListForm onCancel={modalCloseHandler} onSubmit={modalCloseHandler}></MyListsEditListForm>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps)(RecentWishListsCondensedView);
